import { useCallback, useEffect, useLayoutEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { useTheme } from 'components/core/theme-provider';
import {
    Internet,
    Usb,
    MediaImage,
    Xmark,
    BookStack,
    NavArrowDown,
    NavArrowRight,
    Check,
    Refresh,
    Flash,
    Tools,
} from 'iconoir-react';
import { ButtonIds } from '../types/chat';
import { useTranslation } from 'react-i18next';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'components/ui/tooltip';
import { SourcesSelector } from './SourcesSelector';
import gdriveIcon from 'assets/images/gdrive.svg';
import onedriveIcon from 'assets/images/onedrive.svg';
import sharepointIcon from 'assets/images/sharepoint.svg';
import settings2Dark from 'assets/images/settings2Dark.svg';
import settings2White from 'assets/images/settings2White.svg';
import { useIsMobile } from 'hooks/use-mobile';
import { useGetTools } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { ITool } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useConnectorTabTools } from 'pages/tools/use-connector-tab-tools';
import { useConnectorToolLogo } from 'pages/tools/use-connector-tool-logo';
import { computePanelAboveAnchorPosition, computeSubmenuFlyoutPosition } from 'features/chat/utils/submenu-flyout-position';
import { isOpenConnectorEnabled } from 'config/feature-flags';
import { cn } from 'lib/utils';

const TOOLS_FLYOUT_WIDTH = 360;
const TOOLS_FLYOUT_MAX_HEIGHT = 420;

function SelectorConnectorLogo({ tool, isSelected }: { tool: ITool; isSelected: boolean }) {
    const logo = useConnectorToolLogo(tool);

    return (
        <div
            className={cn(
                'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md',
                logo
                    ? 'border border-border/60 bg-true-white p-0.5 dark:bg-card'
                    : isSelected
                      ? 'bg-primary/15'
                      : 'bg-muted/80 text-muted-foreground group-hover/tool:text-foreground'
            )}
        >
            {logo ? (
                <img src={logo} alt="" className="h-full w-full object-contain" />
            ) : (
                <Flash className="h-3.5 w-3.5" strokeWidth={1.8} />
            )}
        </div>
    );
}

const ConnectorsIcon = ({ className }: { className?: string }) => (
    <Usb className={cn('h-4 w-4 shrink-0', className)} strokeWidth={1.5} />
);

const ToolsMenuIcon = ({ className }: { className?: string }) => {
    const { theme } = useTheme();

    return (
        <img
            src={theme === 'dark' ? settings2White : settings2Dark}
            alt=""
            className={cn('h-4 w-4 shrink-0', className)}
        />
    );
};

const pulseAnimation = `
@keyframes brightPulse {
  0%, 100% {
    opacity: 1;
    box-shadow: 0 0 0 0 rgba(109, 109, 109, 0.35);
  }
  50% {
    opacity: 0.85;
    box-shadow: 0 0 0 8px rgba(109, 109, 109, 0);
  }
}
`;

const sourcesPillBorderClass = 'border-[0.5px] border-solid border-black-50 dark:border-black-500';

const toolMenuItemHoverClass = 'rounded-md hover:bg-[#F6F6F6] dark:hover:bg-muted';
const toolMenuItemTextClass = 'text-[#6D6D6D] dark:text-[#ffffff]';
const toolMenuItemLabelClass =
    'font-sans text-sm font-normal leading-[1.2] text-[#3D3D3D] dark:text-[#ffffff]';

interface ToolsSelectorProps {
    isFeatureSelected?: Record<ButtonIds, boolean>;
    onToggleTool: (toolId: ButtonIds) => void;
    disabled?: boolean;
    onOpenSourcesSelector?: () => void;
    selectedSources?: string[];
    isSourcesToolActive?: boolean;
    onToggleSourcesTool?: () => void;
    onSourcesChange?: (sources: string[]) => void;
    sourcesPopoverOpen?: boolean;
    onSourcesPopoverOpenChange?: (open: boolean) => void;
    selectedTools?: string[];
    onSelectedToolsChange?: (tools: string[]) => void;
    /** Close the parent + menu before opening tools on responsive layouts. */
    onMenuClose?: () => void;
}

interface SourceConfig {
    id: string;
    name: string;
    icon?: string;
    color: string;
}

const sourceConfigs: Record<string, SourceConfig> = {
    'document-manager': {
        id: 'document-manager',
        name: 'DOCUMENT_MANAGEMENT_TITLE',
        color: '#8B5CF6',
    },
    'google-drive': {
        id: 'google-drive',
        name: 'GOOGLE_DRIVE',
        icon: gdriveIcon,
        color: '#4285F4',
    },
    'google_drive': {
        id: 'google_drive',
        name: 'GOOGLE_DRIVE',
        icon: gdriveIcon,
        color: '#4285F4',
    },
    'googledrive': {
        id: 'googledrive',
        name: 'GOOGLE_DRIVE',
        icon: gdriveIcon,
        color: '#4285F4',
    },
    'google': {
        id: 'google',
        name: 'GOOGLE_DRIVE',
        icon: gdriveIcon,
        color: '#4285F4',
    },
    'onedrive': {
        id: 'onedrive',
        name: 'ONEDRIVE',
        icon: onedriveIcon,
        color: '#0078D4',
    },
    'microsoft-sharepoint': {
        id: 'microsoft-sharepoint',
        name: 'SHAREPOINT',
        icon: sharepointIcon,
        color: '#0078D4',
    },
    'sharepoint': {
        id: 'sharepoint',
        name: 'SHAREPOINT',
        icon: sharepointIcon,
        color: '#0078D4',
    },
    'dropbox': {
        id: 'dropbox',
        name: 'DROPBOX',
        color: '#0061FF',
    },
    'gmail': {
        id: 'gmail',
        name: 'GMAIL',
        color: '#EA4335',
    },
    'google-calendar': {
        id: 'google-calendar',
        name: 'GOOGLE_CALENDAR',
        color: '#4285F4',
    },
    'google-contacts': {
        id: 'google-contacts',
        name: 'GOOGLE_CONTACTS',
        color: '#4285F4',
    },
    'box': {
        id: 'box',
        name: 'BOX',
        color: '#0061D5',
    },
};

interface ToolConfig {
    id: ButtonIds | 'sources' | 'tools-menu';
    name: string;
    icon: JSX.Element;
    hasSubmenu?: boolean;
}

type AvailableToolType = 'api' | 'mcp_server' | 'composio' | 'open_connector';

const getSourceConfig = (sourceId: string): SourceConfig => {
    return sourceConfigs[sourceId] || {
        id: sourceId,
        name: sourceId,
        color: '#6B7280',
    };
};

const SOURCE_NAME_FALLBACKS: Record<string, string> = {
    DOCUMENT_MANAGEMENT_TITLE: 'Knowledge Base',
    GOOGLE_DRIVE: 'Google Drive',
    ONEDRIVE: 'OneDrive',
    SHAREPOINT: 'SharePoint',
    DROPBOX: 'Dropbox',
    GMAIL: 'Gmail',
    GOOGLE_CALENDAR: 'Google Calendar',
    GOOGLE_CONTACTS: 'Google Contacts',
    BOX: 'Box',
};

const getSourceLabel = (nameKey: string, translate: (key: string) => string) =>
    translate(nameKey) || SOURCE_NAME_FALLBACKS[nameKey] || nameKey;

const buildTools = (translate: (key: string, options?: Record<string, unknown>) => string): ToolConfig[] => [
    {
        id: ButtonIds.SEARCH,
        name: translate('SEARCH') || 'Web search',
        icon: <Internet className="h-4 w-4 shrink-0" strokeWidth={1.5} />,
    },
    {
        id: ButtonIds.CREATE_IMAGE,
        name: translate('GENERATE_IMAGE') || 'Generate image',
        icon: <MediaImage className="h-4 w-4 shrink-0" strokeWidth={1.5} />,
    },
    {
        id: 'tools-menu',
        name: translate('TOOLS') || 'Tools',
        // Same iconoir `Tools` glyph as the sidebar Tools rail item (menu only).
        icon: <Tools className="h-4 w-4 shrink-0" strokeWidth={1.5} />,
        hasSubmenu: true,
    },
];

const XL_BREAKPOINT = 1280;

const useIsXlUp = (): boolean => {
    const [isXlUp, setIsXlUp] = useState<boolean>(false);

    useEffect(() => {
        const mql = window.matchMedia(`(min-width: ${XL_BREAKPOINT}px)`);
        const onChange = (event: MediaQueryListEvent) => {
            setIsXlUp(event.matches);
        };

        setIsXlUp(mql.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    return isXlUp;
};

interface ActiveToolsDisplayProps {
    isFeatureSelected?: Record<ButtonIds, boolean>;
    onToggleTool: (toolId: ButtonIds) => void;
    onOpenSourcesSelector?: () => void;
    selectedSources?: string[];
    isSourcesToolActive?: boolean;
    onToggleSourcesTool?: () => void;
    onSourcesChange?: (sources: string[]) => void;
    sourcesPopoverOpen?: boolean;
    onSourcesPopoverOpenChange?: (open: boolean) => void;
    selectedTools?: string[];
    onSelectedToolsChange?: (tools: string[]) => void;
    className?: string;
}

export const ActiveToolsDisplay = ({
    isFeatureSelected,
    onToggleTool,
    onOpenSourcesSelector,
    selectedSources = [],
    isSourcesToolActive = false,
    onToggleSourcesTool,
    onSourcesChange,
    sourcesPopoverOpen = false,
    onSourcesPopoverOpenChange,
    selectedTools = [],
    onSelectedToolsChange,
    className = '',
}: ActiveToolsDisplayProps) => {
    const { theme } = useTheme();
    const { t } = useTranslation();
    const isMobile = useIsMobile();
    const isXlUp = useIsXlUp();

    const tools: ToolConfig[] = buildTools(t);
    const activeTools = tools.filter((tool) => tool.id !== 'sources' && tool.id !== 'tools-menu' && isFeatureSelected?.[tool.id as ButtonIds]);
    const hasActiveTools = activeTools.length > 0 || isSourcesToolActive || selectedTools.length > 0;

    const handleToolClick = (toolId: ButtonIds | 'sources' | 'tools-menu') => {
        if (toolId === 'sources') {
            onToggleSourcesTool?.();
        } else if (toolId === 'tools-menu') {
            onSelectedToolsChange?.([]);
        } else {
            onToggleTool(toolId);
        }
    };

    if (!hasActiveTools) {
        return null;
    }

    return (
        <div className={`flex flex-wrap items-center gap-2 md:gap-4 ${className}`}>
            <style>{pulseAnimation}</style>
            {isSourcesToolActive && (
                <div style={{ position: 'relative' }}>
                    {selectedSources.length > 0 ? (
                        <div
                            onClick={() => onOpenSourcesSelector?.()}
                            className={`relative flex items-center gap-1.5 h-[34px] px-1 sm:px-2.5 rounded-full shadow-sm hover:bg-muted group cursor-pointer ${sourcesPillBorderClass}`}
                        >
                            <div className="flex items-center gap-1">
                                {selectedSources.slice(0, isMobile ? 1 : 2).map((sourceId) => {
                                    const sourceConfig = getSourceConfig(sourceId);
                                    return (
                                        <TooltipProvider key={sourceId}>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <div
                                                        className="relative"
                                                        onClick={() => onOpenSourcesSelector?.()}
                                                    >
                                                        {sourceConfig.icon ? (
                                                            <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center border">
                                                                <img
                                                                    src={sourceConfig.icon}
                                                                    alt={getSourceLabel(sourceConfig.name, t)}
                                                                    className="w-4 h-4"
                                                                />
                                                            </div>
                                                        ) : (
                                                            <div
                                                                className="w-5 h-5 rounded-full flex items-center justify-center border border-white"
                                                                style={{ backgroundColor: sourceConfig.color }}
                                                            >
                                                                {sourceId === 'document-manager' ? (
                                                                    <BookStack className={`h-3 w-3 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                                                ) : (
                                                                    <ConnectorsIcon className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p className="text-xs">{getSourceLabel(sourceConfig.name, t)}</p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    );
                                })}
                                {selectedSources.length > (isMobile ? 1 : 2) && (
                                    <TooltipProvider>
                                        <Tooltip>
                                            <TooltipTrigger asChild>
                                                <div
                                                    className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium border ${theme === 'dark'
                                                        ? 'bg-gray-600 border-gray-500 text-gray-200'
                                                        : 'bg-gray-200 border-gray-300 text-gray-700'
                                                        }`}
                                                    onClick={() => onOpenSourcesSelector?.()}
                                                >
                                                    +{selectedSources.length - (isMobile ? 1 : 2)}
                                                </div>
                                            </TooltipTrigger>
                                            <TooltipContent>
                                                <div className="text-xs">
                                                    {selectedSources.slice(isMobile ? 1 : 2).map((sourceId) => {
                                                        const sourceConfig = getSourceConfig(sourceId);
                                                        return (
                                                            <div key={sourceId}>
                                                                {getSourceLabel(sourceConfig.name, t)}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </TooltipContent>
                                        </Tooltip>
                                    </TooltipProvider>
                                )}
                            </div>
                            <NavArrowDown className={`h-3 w-3 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                            {selectedSources.length === 0 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onOpenSourcesSelector?.();
                                    }}
                                    className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                        : 'bg-white hover:bg-gray-100 border-primary'
                                        } border shadow-sm`}
                                    aria-label={t('TOOLS_MANAGE_SOURCES') || 'Manage sources'}
                                >
                                    <Xmark className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                </button>
                            )}
                        </div>
                    ) : (
                        <div
                            onClick={() => onOpenSourcesSelector?.()}
                            className={`relative flex items-center gap-2 h-[34px] min-w-[34px] px-3 rounded-full shadow-sm hover:bg-muted group cursor-pointer ${sourcesPillBorderClass}`}
                            style={{
                                animation: 'brightPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                            }}
                        >
                            <ConnectorsIcon className={`h-4 w-4 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                            <span className={`text-sm hidden xl:block font-medium ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'
                                }`}>
                                {t('INTEGRATIONS_KEY') || 'Integrations'}
                            </span>
                            <NavArrowDown className={`h-3 w-3 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleSourcesTool?.();
                                }}
                                className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                    ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                    : 'bg-white hover:bg-gray-100 border-primary'
                                    } border shadow-sm`}
                                aria-label={t('TOOLS_REMOVE_SOURCES') || 'Remove sources tool'}
                            >
                                <Xmark className={`h-3 w-3 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                            </button>
                        </div>
                    )}

                    {/* Mount SourcesSelector ONLY for non-desktop to avoid duplicate popovers and wrong anchoring */}
                    {!isXlUp && (
                        <div style={{ position: 'absolute', left: 0, top: 0, opacity: 0, pointerEvents: 'none' }}>
                            <SourcesSelector
                                selectedSources={selectedSources}
                                onSourcesChange={onSourcesChange || (() => { /* no-op */ })}
                                disabled={false}
                                externalOpen={sourcesPopoverOpen}
                                onExternalOpenChange={onSourcesPopoverOpenChange}
                            />
                        </div>
                    )}
                </div>
            )}

            {activeTools.map((tool) => (
                <TooltipProvider key={tool.id}>

                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div
                                className={`relative flex items-center gap-2 h-[34px] min-w-[34px] justify-center px-3 py-2 rounded-full border-[0.25px] shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'

                                    ? 'border-black-500'
                                    : 'border-black-50'
                                    }`}
                            >
                                <div className={`${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`}>
                                    {tool.icon}
                                </div>
                                <span className={toolMenuItemLabelClass}>{tool.name}</span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleToolClick(tool.id);
                                    }}
                                    className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                        : 'bg-white hover:bg-gray-100 border-primary'
                                        } border shadow-sm`}
                                    aria-label={t('TOOLS_REMOVE_TOOL', { tool: tool.name }) || `Remove ${tool.name}`}
                                >
                                    <Xmark className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                </button>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="text-xs">{tool.name}</p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            ))}

            {selectedTools.length > 0 && (
                <TooltipProvider>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <div
                                className={`relative flex items-center gap-2 h-[34px] min-w-[34px] justify-center px-3 rounded-full border-[0.25px] shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'
                                    ? 'border-black-500'
                                    : 'border-black-50'
                                    }`}
                            >
                                <div className={`${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`}>
                                    <ToolsMenuIcon />
                                </div>
                                <span className={`text-sm font-medium ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`}>
                                    {selectedTools.length}
                                </span>
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onSelectedToolsChange?.([]);
                                    }}
                                    className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                        ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                        : 'bg-white hover:bg-gray-100 border-primary'
                                        } border shadow-sm`}
                                    aria-label={t('TOOLS_REMOVE_TOOLS') || 'Remove tools'}
                                >
                                    <Xmark className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                </button>
                            </div>
                        </TooltipTrigger>
                        <TooltipContent>
                            <p className="text-xs">
                                {t('TOOLS_SELECTED_COUNT', { count: selectedTools.length }) ||
                                    `${selectedTools.length} tools selected`}
                            </p>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            )}
        </div>
    );
};

export const ToolsSelector = ({
    isFeatureSelected,
    onToggleTool,
    disabled = false,
    onOpenSourcesSelector,
    selectedSources = [],
    isSourcesToolActive = false,
    onToggleSourcesTool,
    onSourcesChange,
    sourcesPopoverOpen = false,
    onSourcesPopoverOpenChange,
    selectedTools = [],
    onSelectedToolsChange,
    onMenuClose,
}: ToolsSelectorProps) => {
    const [open, setOpen] = useState(false);
    const [toolType, setToolType] = useState<AvailableToolType>('composio');
    const [toolsMenuOpen, setToolsMenuOpen] = useState(false);
    const [desktopToolsMenuOpen, setDesktopToolsMenuOpen] = useState(false);
    const [mobileModalOpen, setMobileModalOpen] = useState(false);
    const [toolsFlyoutPosition, setToolsFlyoutPosition] = useState<{ top: number; left: number } | null>(null);
    const [desktopToolsPanelPosition, setDesktopToolsPanelPosition] = useState<{ top: number; left: number } | null>(null);
    const sidebarToolsMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const toolsMenuTriggerRef = useRef<HTMLButtonElement>(null);
    const desktopToolsIndicatorRef = useRef<HTMLDivElement>(null);
    const { theme } = useTheme();
    const { t } = useTranslation();

    const tools: ToolConfig[] = buildTools(t);

    const isMobile = useIsMobile();
    const isXlUp = useIsXlUp();
    const { data: toolsData, isLoading: isPageLoading } = useGetTools({
        tool_type: toolType,
        page: 1,
        page_size: 50,
    });
    const { tools: availableTools, isLoading: isPromotedLoading } = useConnectorTabTools(
        toolType,
        toolsData?.tools ?? []
    );
    const isToolsLoading = isPageLoading || isPromotedLoading;

    const handleToolClick = (toolId: ButtonIds | 'sources' | 'tools-menu', closeMenu?: () => void) => {
        if (toolId === 'sources') {
            onToggleSourcesTool?.();
            closeMenu?.();
            setOpen(false);
        } else if (toolId === 'tools-menu') {
            setToolsMenuOpen((prev) => !prev);
        } else {
            // Mutual exclusivity: Web Search/Create Image and Tools cannot be active at same time
            if ((toolId === ButtonIds.SEARCH || toolId === ButtonIds.CREATE_IMAGE) && selectedTools.length > 0) {
                // Deselect all tools when enabling Web Search or Create Image
                onSelectedToolsChange?.([]);
            }

            // Mutual exclusivity: Web Search and Create Image cannot be active at same time
            if (toolId === ButtonIds.SEARCH && isFeatureSelected?.[ButtonIds.CREATE_IMAGE]) {
                onToggleTool(ButtonIds.CREATE_IMAGE); // Turn off Create Image
            } else if (toolId === ButtonIds.CREATE_IMAGE && isFeatureSelected?.[ButtonIds.SEARCH]) {
                onToggleTool(ButtonIds.SEARCH); // Turn off Web Search
            }

            onToggleTool(toolId);
            closeMenu?.();
            setOpen(false);
        }
    };
    const handleExternalToolToggle = (toolId: string) => {
        const nextTools = selectedTools.includes(toolId)
            ? selectedTools.filter((id) => id !== toolId)
            : [...selectedTools, toolId];

        // Mutual exclusivity: Disable Web Search and Create Image when selecting any tool
        if (nextTools.length > 0) {
            if (isFeatureSelected?.[ButtonIds.SEARCH]) {
                onToggleTool(ButtonIds.SEARCH);
            }
            if (isFeatureSelected?.[ButtonIds.CREATE_IMAGE]) {
                onToggleTool(ButtonIds.CREATE_IMAGE);
            }
        }

        onSelectedToolsChange?.(nextTools);
    };

    const handleExternalToolPointerDown = (
        event: React.PointerEvent<HTMLButtonElement>,
        toolId: string
    ) => {
        event.preventDefault();
        event.stopPropagation();
        handleExternalToolToggle(toolId);
    };

    const handleToolTypePointerDown = (
        event: React.PointerEvent<HTMLButtonElement>,
        type: AvailableToolType
    ) => {
        event.preventDefault();
        event.stopPropagation();
        setToolType(type);
    };
    // Get active tools (excluding sources, we'll handle them separately)
    const activeTools = tools.filter(tool => {
        if (tool.id === 'sources') return false; // Don't include generic sources
        if (tool.id === 'tools-menu') return false;
        return isFeatureSelected?.[tool.id as ButtonIds];
    });
    const hasActiveTools = activeTools.length > 0 || isSourcesToolActive || selectedTools.length > 0;

    const updateToolsFlyoutPosition = useCallback(() => {
        if (!toolsMenuOpen || !toolsMenuTriggerRef.current) {
            setToolsFlyoutPosition(null);
            return;
        }

        setToolsFlyoutPosition(
            computeSubmenuFlyoutPosition(toolsMenuTriggerRef.current.getBoundingClientRect(), {
                width: TOOLS_FLYOUT_WIDTH,
                maxHeight: TOOLS_FLYOUT_MAX_HEIGHT,
            })
        );
    }, [toolsMenuOpen]);

    useLayoutEffect(() => {
        updateToolsFlyoutPosition();
    }, [updateToolsFlyoutPosition]);

    useEffect(() => {
        if (!toolsMenuOpen) return;

        window.addEventListener('resize', updateToolsFlyoutPosition);
        window.addEventListener('scroll', updateToolsFlyoutPosition, true);
        return () => {
            window.removeEventListener('resize', updateToolsFlyoutPosition);
            window.removeEventListener('scroll', updateToolsFlyoutPosition, true);
        };
    }, [toolsMenuOpen, updateToolsFlyoutPosition]);

    const updateDesktopToolsPanelPosition = useCallback(() => {
        if (!desktopToolsMenuOpen || !desktopToolsIndicatorRef.current) {
            setDesktopToolsPanelPosition(null);
            return;
        }

        setDesktopToolsPanelPosition(
            computePanelAboveAnchorPosition(desktopToolsIndicatorRef.current.getBoundingClientRect(), {
                width: TOOLS_FLYOUT_WIDTH,
                maxHeight: TOOLS_FLYOUT_MAX_HEIGHT,
            })
        );
    }, [desktopToolsMenuOpen]);

    useLayoutEffect(() => {
        updateDesktopToolsPanelPosition();
    }, [updateDesktopToolsPanelPosition]);

    useEffect(() => {
        if (!desktopToolsMenuOpen) return;

        window.addEventListener('resize', updateDesktopToolsPanelPosition);
        window.addEventListener('scroll', updateDesktopToolsPanelPosition, true);
        return () => {
            window.removeEventListener('resize', updateDesktopToolsPanelPosition);
            window.removeEventListener('scroll', updateDesktopToolsPanelPosition, true);
        };
    }, [desktopToolsMenuOpen, updateDesktopToolsPanelPosition]);

    useEffect(() => {
        if (!desktopToolsMenuOpen) return;

        const handlePointerDown = (event: PointerEvent) => {
            const target = event.target as HTMLElement | null;
            if (
                target?.closest('[data-tools-selector-flyout]') ||
                target?.closest('[data-tools-selector-dialog]') ||
                desktopToolsIndicatorRef.current?.contains(target ?? null)
            ) {
                return;
            }
            setDesktopToolsMenuOpen(false);
        };

        document.addEventListener('pointerdown', handlePointerDown);
        return () => document.removeEventListener('pointerdown', handlePointerDown);
    }, [desktopToolsMenuOpen]);

    const toggleDesktopToolsIndicatorPanel = () => {
        setOpen(false);
        setToolsMenuOpen(false);
        setDesktopToolsMenuOpen((prev) => !prev);
    };

    const openMobileToolsPanel = () => {
        setToolsMenuOpen(false);
        setDesktopToolsMenuOpen(false);
        globalThis.setTimeout(() => {
            setMobileModalOpen(true);
        }, 0);
    };

    const renderToolsFlyoutPanel = () => {
        if (!isXlUp || !toolsMenuOpen || !toolsFlyoutPosition) return null;

        return createPortal(
            <div
                data-plus-menu-flyout=""
                data-tools-selector-flyout=""
                className="pointer-events-auto fixed z-[200] overflow-hidden rounded-xl border border-border bg-popover shadow-lg"
                style={{
                    top: toolsFlyoutPosition.top,
                    left: toolsFlyoutPosition.left,
                    width: TOOLS_FLYOUT_WIDTH,
                }}
                onPointerDown={(e) => e.stopPropagation()}
                onMouseEnter={() => {
                    if (sidebarToolsMenuTimeoutRef.current) {
                        clearTimeout(sidebarToolsMenuTimeoutRef.current);
                        sidebarToolsMenuTimeoutRef.current = null;
                    }
                    setToolsMenuOpen(true);
                }}
            >
                {renderAvailableToolsPanel()}
            </div>,
            document.body
        );
    };

    const renderDesktopToolsIndicatorPanel = () => {
        if (!desktopToolsMenuOpen || !desktopToolsPanelPosition) return null;

        return createPortal(
            <div
                data-tools-selector-flyout=""
                data-tools-selector-dialog=""
                className="pointer-events-auto fixed z-[200] overflow-hidden rounded-xl border border-border bg-popover shadow-lg"
                style={{
                    top: desktopToolsPanelPosition.top,
                    left: desktopToolsPanelPosition.left,
                    width: TOOLS_FLYOUT_WIDTH,
                    maxHeight: TOOLS_FLYOUT_MAX_HEIGHT,
                }}
                onPointerDown={(e) => e.stopPropagation()}
            >
                {renderAvailableToolsPanel()}
            </div>,
            document.body
        );
    };

    const toolTypeTabs: { id: typeof toolType; label: string }[] = [
        { id: 'composio', label: t('TOOLS_TAB_CONNECTOR') || 'Connector' },
        { id: 'mcp_server', label: t('TOOLS_TAB_MCP') || 'MCP' },
        { id: 'api', label: t('TOOLS_TAB_API') || 'API' },
        ...(isOpenConnectorEnabled
            ? [{ id: 'open_connector' as const, label: t('OPEN_CONNECTORS') || 'Open Connectors' }]
            : []),
    ];

    const emptyToolsHint =
        toolType === 'api'
            ? t('TOOLS_NO_API_FOUND') || 'No API tools found'
            : toolType === 'composio'
                ? t('TOOLS_NO_COMPOSIO_FOUND') || 'No Composio tools found'
                : toolType === 'open_connector'
                    ? t('TOOLS_NO_OPEN_CONNECTOR_FOUND') || 'No open connector tools found'
                    : t('TOOLS_NO_MCP_FOUND') || 'No MCP servers found';

    const closeAvailableToolsPanel = () => {
        setToolsMenuOpen(false);
        setDesktopToolsMenuOpen(false);
        setMobileModalOpen(false);
        setOpen(false);
        onMenuClose?.();
    };

    const renderAvailableToolsPanel = (options?: { hideTitle?: boolean }) => (
        <div className="w-full xl:w-[95vw] xl:max-w-[360px] lg:w-full xl:rounded-xl xl:border xl:border-border bg-popover text-popover-foreground xl:shadow-lg h-full flex flex-col max-h-[80vh]">
            <div className="flex flex-col h-full xl:h-auto xl:max-h-[400px]">
                <div
                    className={cn(
                        'px-3 pt-3 pb-2.5 space-y-2.5 flex-shrink-0 border-b border-border/40',
                        options?.hideTitle && 'pt-2.5'
                    )}
                >
                    {!options?.hideTitle && (
                        <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-medium text-muted-foreground tracking-wide">
                                {t('TOOLS_AVAILABLE_TITLE') || 'Available Tools'}
                            </p>
                            <button
                                type="button"
                                onPointerDown={(event) => {
                                    event.preventDefault();
                                    event.stopPropagation();
                                    closeAvailableToolsPanel();
                                }}
                                className={cn(
                                    'flex shrink-0 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground transition-colors',
                                    selectedTools.length > 0 ? 'h-6 px-2' : 'h-6 w-6'
                                )}
                                aria-label={
                                    selectedTools.length > 0
                                        ? t('DONE') || 'Done'
                                        : t('CLOSE') || 'Close'
                                }
                            >
                                {selectedTools.length > 0 ? (
                                    <span className="text-sm font-medium text-foreground">
                                        {t('DONE') || 'Done'}
                                    </span>
                                ) : (
                                    <Xmark className="h-3.5 w-3.5" strokeWidth={2} />
                                )}
                            </button>
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <div
                            role="tablist"
                            aria-label={t('TOOLS_AVAILABLE_TITLE') || 'Available Tools'}
                            className="inline-flex min-w-full gap-0.5 rounded-lg bg-muted/60 p-0.5"
                        >
                            {toolTypeTabs.map((tab) => {
                                const isActive = toolType === tab.id;
                                return (
                                    <button
                                        key={tab.id}
                                        type="button"
                                        role="tab"
                                        aria-selected={isActive}
                                        onPointerDown={(event) => handleToolTypePointerDown(event, tab.id)}
                                        className={cn(
                                            'min-w-[50px] shrink-0 grow whitespace-nowrap rounded-md px-2 py-1.5 text-center text-xs font-medium transition-colors',
                                            isActive
                                                ? 'bg-background text-foreground shadow-sm'
                                                : 'text-muted-foreground hover:text-foreground'
                                        )}
                                        title={tab.label}
                                    >
                                        {tab.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="flex-1 min-h-[220px] overflow-y-auto overflow-x-hidden px-1.5 py-1.5 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-muted-foreground/20 [&::-webkit-scrollbar-thumb]:rounded-full">
                    {isToolsLoading ? (
                        <div className="flex h-full items-center justify-center">
                            <Refresh className="h-5 w-5 animate-spin text-muted-foreground" />
                        </div>
                    ) : availableTools.length > 0 ? (
                        <div className="flex flex-col gap-0.5">
                            {availableTools.map((tool) => {
                                const isSelected = selectedTools.includes(tool.id);

                                return (
                                    <button
                                        key={tool.id}
                                        type="button"
                                        aria-label={tool.name}
                                        aria-pressed={isSelected}
                                        onPointerDown={(event) => handleExternalToolPointerDown(event, tool.id)}
                                        className={cn(
                                            'group/tool flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left transition-colors',
                                            isSelected
                                                ? 'bg-primary/10 text-foreground'
                                                : 'hover:bg-muted/70'
                                        )}
                                    >
                                        {toolType === 'composio' || toolType === 'open_connector' ? (
                                            <SelectorConnectorLogo
                                                tool={tool}
                                                isSelected={isSelected}
                                            />
                                        ) : (
                                            <div
                                                className={cn(
                                                    'mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center overflow-hidden rounded-md',
                                                    isSelected
                                                        ? 'bg-primary/15'
                                                        : 'bg-muted/80 text-muted-foreground group-hover/tool:text-foreground'
                                                )}
                                            >
                                                <Flash className="h-3.5 w-3.5" strokeWidth={1.8} />
                                            </div>
                                        )}

                                        <div className="min-w-0 flex-1">
                                            <p
                                                className={cn(
                                                    'text-sm font-medium leading-snug truncate',
                                                   
                                                )}
                                                title={tool.name}
                                            >
                                                {tool.name}
                                            </p>
                                            {tool.description && (
                                                <p className="mt-0.5 text-xs text-muted-foreground line-clamp-2 leading-snug">
                                                    {tool.description}
                                                </p>
                                            )}
                                        </div>

                                        <div
                                            className={cn(
                                                'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-colors',
                                                isSelected
                                                    ? 'border-primary bg-primary text-primary-foreground'
                                                    : 'border-border/70 bg-transparent'
                                            )}
                                            aria-hidden
                                        >
                                            {isSelected && (
                                                <Check className="h-3 w-3" strokeWidth={2.5} />
                                            )}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="flex flex-col items-center justify-center h-full gap-1.5 px-4 text-center">
                            <div className="flex h-9 w-9 items-center justify-center rounded-full bg-muted/60">
                                <Flash className="h-4 w-4 text-muted-foreground/60" />
                            </div>
                            <p className="text-sm text-muted-foreground">
                                {t('TOOLS_NONE_AVAILABLE') || 'No tools available'}
                            </p>
                            <p className="text-xs text-muted-foreground/70">{emptyToolsHint}</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    const renderDesktopToolsMenuList = (closeMenu?: () => void) => (
        <div
            className="relative"
            onMouseLeave={() => {
                sidebarToolsMenuTimeoutRef.current = setTimeout(() => {
                    setToolsMenuOpen(false);
                }, 800);
            }}
        >
            <div className="flex flex-col gap-[6px]">
                {tools.map((tool) => {
                    const isActive = tool.id === 'sources'
                        ? isSourcesToolActive
                        : tool.id === 'tools-menu'
                            ? selectedTools.length > 0
                            : isFeatureSelected?.[tool.id as ButtonIds];

                    return (
                        <button
                            key={tool.id}
                            ref={tool.id === 'tools-menu' ? toolsMenuTriggerRef : undefined}
                            onClick={() => handleToolClick(tool.id, closeMenu)}
                            onMouseEnter={() => {
                                if (sidebarToolsMenuTimeoutRef.current) {
                                    clearTimeout(sidebarToolsMenuTimeoutRef.current);
                                    sidebarToolsMenuTimeoutRef.current = null;
                                }
                                if (tool.id === 'tools-menu') {
                                    setToolsMenuOpen(true);
                                } else {
                                    setToolsMenuOpen(false);
                                }
                            }}
                            className={`flex w-full items-center gap-3 px-1 py-1.5 transition-colors text-left ${toolMenuItemHoverClass} ${isActive ? 'bg-[#F6F6F6] dark:bg-muted' : ''}`}
                        >
                            <div className={`flex h-4 w-4 shrink-0 items-center justify-center ${toolMenuItemTextClass}`}>
                                {tool.icon}
                            </div>
                            <div className="flex flex-1 items-center justify-between gap-2">
                                <span className={toolMenuItemLabelClass}>
                                    {tool.name}
                                </span>
                                {tool.hasSubmenu && (
                                    <div className="flex items-center gap-1">
                                        {selectedTools.length > 0 && (
                                            <span className="text-xs font-medium text-muted-foreground">
                                                {selectedTools.length}
                                            </span>
                                        )}
                                        <NavArrowRight className="h-4 w-4 text-muted-foreground" />
                                    </div>
                                )}
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );

    return (
        <div className="flex items-center gap-2">
            {/* Tools Button - desktop (popover) */}
            <div className="xl:flex hidden">
                <Popover open={open} onOpenChange={(nextOpen) => {
                    setOpen(nextOpen);
                    if (!nextOpen) {
                        setToolsMenuOpen(false);
                    } else {
                        setDesktopToolsMenuOpen(false);
                    }
                }}>
                    <TooltipProvider delayDuration={300}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              disabled={disabled}
                              aria-pressed="false"
                              aria-label={t('TOOLS_MENU') || 'Tools'}
                              className={
                                `flex h-7 w-7 min-w-7 shrink-0 items-center justify-center p-0 hover:rounded-full hover:bg-muted transition-colors disabled:opacity-30 disabled:cursor-not-allowed 2xl:h-8 2xl:min-w-8 2xl:w-8 ` +
                                (theme === 'dark' ? 'text-[#ffffff]' : 'text-[#6D6D6D]') +
                                (hasActiveTools ? (theme === 'dark' ? ' border-white/50' : ' border-black-50') : '')
                              }
                            >
                              <img
                                src={theme === 'dark' ? settings2White : settings2Dark}
                                alt=""
                                className="h-4 w-4 shrink-0 2xl:h-5 2xl:w-5"
                              />
                            </button>
                          </PopoverTrigger>
                        </TooltipTrigger>
                        <TooltipContent side="top">{t('TOOLS_MENU', { defaultValue: 'Tools' })}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                    <PopoverContent
                        className="w-52"
                        align="start"
                        side="top"
                        sideOffset={10}
                        collisionPadding={16}
                        onInteractOutside={(e) => {
                            const target = e.target as HTMLElement;
                            if (
                                target.closest('[data-plus-menu-flyout]') ||
                                target.closest('[data-tools-selector-flyout]') ||
                                target.closest('[data-tools-selector-dialog]')
                            ) {
                                e.preventDefault();
                            }
                        }}
                        onPointerDownOutside={(e) => {
                            const target = e.target as HTMLElement;
                            if (
                                target.closest('[data-plus-menu-flyout]') ||
                                target.closest('[data-tools-selector-flyout]') ||
                                target.closest('[data-tools-selector-dialog]')
                            ) {
                                e.preventDefault();
                            }
                        }}
                    >
                        {renderDesktopToolsMenuList(() => setOpen(false))}
                    </PopoverContent>
                </Popover>
            </div>

            {/* Tools list - mobile/tablet (no popover) */}
            <div className="xl:hidden flex flex-col gap-[6px] w-full">
                {tools.map((tool) => {
                    const isActive = tool.id === 'sources'
                        ? isSourcesToolActive
                        : tool.id === 'tools-menu'
                            ? selectedTools.length > 0
                            : isFeatureSelected?.[tool.id as ButtonIds];

                    return (
                        <div key={tool.id}>
                            <button
                                disabled={disabled}
                                onPointerDown={(e) => e.stopPropagation()}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (tool.id === 'tools-menu') {
                                        openMobileToolsPanel();
                                    } else {
                                        handleToolClick(tool.id);
                                    }
                                }}
                                className={`flex w-full items-center gap-3 px-1 py-1.5 transition-colors text-left ${toolMenuItemHoverClass} ${isActive ? 'bg-muted' : ''}`}
                            >
                                <div className={`flex items-center justify-center ${toolMenuItemTextClass}`}>
                                    {tool.icon}
                                </div>
                                <div className="flex flex-1 items-center justify-between gap-2">
                                    <span className={toolMenuItemLabelClass}>
                                        {tool.name}
                                    </span>
                                    {tool.hasSubmenu && (
                                        <div className="flex items-center gap-1">
                                            {selectedTools.length > 0 && (
                                                <span className="text-xs font-medium text-muted-foreground">
                                                    {selectedTools.length}
                                                </span>
                                            )}
                                            <NavArrowRight className="h-4 w-4 text-muted-foreground" />
                                        </div>
                                    )}
                                </div>
                            </button>
                        </div>
                    );
                })}
            </div>

            {/* Mobile Tools Modal */}
            <Dialog
                open={mobileModalOpen}
                onOpenChange={(open) => {
                    setMobileModalOpen(open);
                    if (!open) {
                        onMenuClose?.();
                    }
                }}
            >
                <DialogContent
                    data-tools-selector-dialog=""
                    className="z-[210] flex h-[min(520px,75vh)] w-[min(420px,92vw)] max-w-[420px] flex-col p-0"
                    overlayClassName="z-[200]"
                    hideClose
                    onPointerDown={(e) => e.stopPropagation()}
                >
                    <DialogHeader className="px-4 pt-4 pb-2 flex-shrink-0 border-b border-border/30">
                        <div className="flex items-center justify-between">
                            <DialogTitle className="text-base font-normal">
                                {t('TOOLS_AVAILABLE_TITLE') || 'Available Tools'}
                            </DialogTitle>
                            <button
                                type="button"
                                onClick={() => setMobileModalOpen(false)}
                                className={cn(
                                    'flex items-center justify-center rounded-full hover:bg-muted transition-colors',
                                    selectedTools.length > 0 ? 'h-8 px-3' : 'h-8 w-8'
                                )}
                                aria-label={
                                    selectedTools.length > 0
                                        ? t('DONE') || 'Done'
                                        : t('CLOSE') || 'Close'
                                }
                            >
                                {selectedTools.length > 0 ? (
                                    <span className="text-sm font-medium">
                                        {t('DONE') || 'Done'}
                                    </span>
                                ) : (
                                    <Xmark className="h-5 w-5" />
                                )}
                            </button>
                        </div>
                    </DialogHeader>
                    <div className="flex-1 overflow-hidden flex flex-col">
                        {renderAvailableToolsPanel({ hideTitle: true })}
                    </div>
                </DialogContent>
            </Dialog>

            {/* Active Tools Display - desktop only (mount only on xl+ to avoid hidden popover anchors) */}
            {isXlUp && hasActiveTools && (
                <div className="hidden lg:flex flex-wrap items-center gap-4">
                    {/* Display other active tools and Tools button first when document-manager is selected */}
                    {selectedSources.includes('document-manager') && activeTools.map((tool) => (
                        <TooltipProvider key={tool.id}>

                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div
                                        className={`relative flex items-center gap-2 h-[34px] min-w-[34px] justify-center px-3 py-2 rounded-full border-[0.25px] shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'

                                            ? 'border-black-500'
                                            : 'border-black-50'
                                            }`}
                                    >
                                        <div className={`${theme === 'dark' ? '!text-[#ffffff]' : 'text-[#3D3D3D]'}`}>
                                            {tool.icon}
                                        </div>
                                        {/* Hide text when Documents source is selected to save space for Select Document button */}
                                        {!selectedSources.includes('document-manager') && (
                                            <span className={toolMenuItemLabelClass}>{tool.name}</span>
                                        )}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToolClick(tool.id);
                                            }}
                                            className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                                ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                                : 'bg-white hover:bg-gray-100 border-primary'
                                                } border shadow-sm`}
                                            aria-label={t('TOOLS_REMOVE_TOOL', { tool: tool.name }) || `Remove ${tool.name}`}
                                        >
                                            <Xmark className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                        </button>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="text-xs">{tool.name}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ))}

                    {/* Display Tools button before Sources when document-manager is selected */}
                    {selectedSources.includes('document-manager') && selectedTools.length > 0 && (
                        <div style={{ position: 'relative' }}>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            ref={desktopToolsIndicatorRef}
                                            onClick={toggleDesktopToolsIndicatorPanel}
                                            className={`relative flex items-center gap-2 h-[34px] min-w-[34px] justify-center px-3 rounded-full border-[0.25px] shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'
                                                ? 'border-black-500'
                                                : 'border-black-50'
                                                }`}
                                        >
                                            <div className={`${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`}>
                                                <ToolsMenuIcon />
                                            </div>
                                            <span className={`text-sm font-medium ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`}>
                                                {t('TOOLS_WITH_COUNT', { count: selectedTools.length }) ||
                                                    `Tools (${selectedTools.length})`}
                                            </span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSelectedToolsChange?.([]);
                                                }}
                                                className={`absolute -top-1 -right-1 w-4 h-4 rounded-full items-center justify-center transition-all duration-200 hover:scale-110 flex md:opacity-70 md:group-hover:opacity-100 ${theme === 'dark'
                                                    ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                                    : 'bg-white hover:bg-gray-100 border-primary'
                                                    } border shadow-sm`}
                                                aria-label={t('TOOLS_REMOVE_TOOLS') || 'Remove tools'}
                                            >
                                                <Xmark className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                            </button>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p className="text-xs">
                                            {t('TOOLS_SELECTED_COUNT', { count: selectedTools.length }) ||
                                                `${selectedTools.length} tools selected`}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    )}

                    {/* Sources tool button with popover */}
                    {isSourcesToolActive && (
                        <div style={{ position: 'relative' }}>
                            <style>{pulseAnimation}</style>
                            {selectedSources.length > 0 ? (
                                /* Merged button showing source icons without text */
                                <div
                                    onClick={() => onOpenSourcesSelector?.()}
                                    className={`relative flex items-center gap-1.5 px-1.5 lg:px-2.5 h-[34px] rounded-full shadow-sm hover:bg-muted group cursor-pointer ${sourcesPillBorderClass}`}
                                >
                                    <div className="flex items-center gap-1">
                                        {selectedSources.slice(0, isMobile ? 1 : 2).map((sourceId) => {
                                            const sourceConfig = getSourceConfig(sourceId);
                                            return (
                                                <TooltipProvider key={sourceId}>
                                                    <Tooltip>
                                                        <TooltipTrigger asChild>
                                                            <div
                                                                className="relative"
                                                                onClick={() => onOpenSourcesSelector?.()}
                                                            >
                                                                {sourceConfig.icon ? (
                                                                    <div className="w-5 h-5 rounded-full bg-white flex items-center justify-center border">
                                                                        <img
                                                                            src={sourceConfig.icon}
                                                                            alt={getSourceLabel(sourceConfig.name, t)}
                                                                            className="w-4 h-4"
                                                                        />
                                                                    </div>
                                                                ) : (
                                                                    <div
                                                                        className="w-5 h-5 rounded-full flex items-center justify-center border border-white"
                                                                        style={{ backgroundColor: sourceConfig.color }}
                                                                    >
                                                                        {sourceId === 'document-manager' ? (
                                                                            <BookStack className="h-3 w-3 text-[#ffffff]" />
                                                                        ) : (
                                                                            <ConnectorsIcon className="h-2.5 w-2.5 text-[#ffffff]" />
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </TooltipTrigger>
                                                        <TooltipContent>
                                                            <p className="text-xs">{getSourceLabel(sourceConfig.name, t)}</p>
                                                        </TooltipContent>
                                                    </Tooltip>
                                                </TooltipProvider>
                                            );
                                        })}
                                        {selectedSources.length > (isMobile ? 1 : 2) && (
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <div
                                                            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-medium border ${theme === 'dark'
                                                                ? 'bg-gray-600 border-gray-500 text-gray-200'
                                                                : 'bg-gray-200 border-gray-300 text-gray-700'
                                                                }`}
                                                            onClick={() => onOpenSourcesSelector?.()}
                                                        >
                                                            +{selectedSources.length - (isMobile ? 1 : 2)}
                                                        </div>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <div className="text-xs">
                                                            {selectedSources.slice(isMobile ? 1 : 2).map((sourceId) => {
                                                                const sourceConfig = getSourceConfig(sourceId);
                                                                return (
                                                                    <div key={sourceId}>
                                                                        {getSourceLabel(sourceConfig.name, t)}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        )}
                                    </div>
                                    <NavArrowDown className={`h-3 w-3 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                    {selectedSources.length == 0 && <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onOpenSourcesSelector?.();
                                        }}
                                        className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                            ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                            : 'bg-white hover:bg-gray-100 border-primary'
                                            } border shadow-sm`}
                                        aria-label={t('TOOLS_MANAGE_SOURCES') || 'Manage sources'}
                                    >
                                        <Xmark className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-primary' : 'text-primary'}`} />
                                    </button>}
                                </div>
                            ) : (
                                /* Sources button with text and dropdown when no sources selected */
                                <div
                                    onClick={() => onOpenSourcesSelector?.()}
                                    className={`relative flex items-center gap-2 h-[34px] min-w-[34px] px-3 rounded-full shadow-sm hover:bg-muted group cursor-pointer ${sourcesPillBorderClass}`}
                                    style={{
                                        animation: 'brightPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                                    }}
                                >
                                    <ConnectorsIcon className={`h-4 w-4 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                    <span className={`text-sm font-medium ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'
                                        }`}>
                                        {t('INTEGRATIONS_KEY') || 'Integrations'}
                                    </span>
                                    <NavArrowDown className={`h-3 w-3 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            onToggleSourcesTool?.();
                                        }}
                                        className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                            ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                            : 'bg-white hover:bg-gray-100 border-primary'
                                            } border shadow-sm`}
                                        aria-label={t('TOOLS_REMOVE_SOURCES') || 'Remove sources tool'}
                                    >
                                        <Xmark className={`h-3 w-3 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                    </button>
                                </div>
                            )
                            }

                            {/* SourcesSelector positioned below the Sources button - hidden trigger */}
                            <div style={{ position: 'absolute', left: 0, top: 0, marginTop: '8px', zIndex: 50, opacity: 0, pointerEvents: 'none' }}>
                                <SourcesSelector
                                    selectedSources={selectedSources}
                                    onSourcesChange={onSourcesChange || (() => { /* no-op */ })}
                                    disabled={false}
                                    externalOpen={sourcesPopoverOpen}
                                    onExternalOpenChange={onSourcesPopoverOpenChange}
                                />
                            </div>
                        </div>
                    )}

                    {/* Display other active tools (Deep research, Create image) - only when document-manager is NOT selected */}
                    {!selectedSources.includes('document-manager') && activeTools.map((tool) => (
                        <TooltipProvider key={tool.id}>

                            <Tooltip>
                                <TooltipTrigger asChild>
                                    <div
                                        className={`relative flex items-center gap-2 h-[34px] min-w-[34px] justify-center px-3 py-2 rounded-full border-[0.25px] shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'

                                            ? 'border-black-500'
                                            : 'border-black-50'
                                            }`}
                                    >
                                        <div className={`${theme === 'dark' ? '!text-[#ffffff]' : 'text-[#3D3D3D]'}`}>
                                            {tool.icon}
                                        </div>
                                        {/* Hide text when Documents source is selected to save space for Select Document button */}
                                        {!selectedSources.includes('document-manager') && (
                                            <span className={toolMenuItemLabelClass}>{tool.name}</span>
                                        )}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleToolClick(tool.id);
                                            }}
                                            className={`absolute -top-1 -right-1 w-4 h-4 rounded-full flex items-center justify-center transition-opacity opacity-0 group-hover:opacity-100 ${theme === 'dark'
                                                ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                                : 'bg-white hover:bg-gray-100 border-primary'
                                                } border shadow-sm`}
                                            aria-label={t('TOOLS_REMOVE_TOOL', { tool: tool.name }) || `Remove ${tool.name}`}
                                        >
                                            <Xmark className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                        </button>
                                    </div>
                                </TooltipTrigger>
                                <TooltipContent>
                                    <p className="text-xs">{tool.name}</p>
                                </TooltipContent>
                            </Tooltip>
                        </TooltipProvider>
                    ))}

                    {/* Display selected tools count - only when document-manager is NOT selected */}
                    {!selectedSources.includes('document-manager') && selectedTools.length > 0 && (
                        <div style={{ position: 'relative' }}>
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger asChild>
                                        <div
                                            ref={desktopToolsIndicatorRef}
                                            onClick={toggleDesktopToolsIndicatorPanel}
                                            className={`relative flex items-center gap-2 h-[34px] min-w-[34px] justify-center px-3 rounded-full border-[0.25px] shadow-sm hover:bg-muted group cursor-pointer ${theme === 'dark'
                                                ? 'border-black-500'
                                                : 'border-black-50'
                                                }`}
                                        >
                                            <div className={`${theme === 'dark' ? '!text-[#ffffff]' : 'text-[#3D3D3D]'}`}>
                                                <ToolsMenuIcon />
                                            </div>
                                            <span className={`text-sm font-medium ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`}>
                                                {t('TOOLS_WITH_COUNT', { count: selectedTools.length }) ||
                                                    `Tools (${selectedTools.length})`}
                                            </span>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    onSelectedToolsChange?.([]);
                                                }}
                                                className={`absolute -top-1 -right-1 w-4 h-4 rounded-full items-center justify-center transition-all duration-200 hover:scale-110 flex md:opacity-70 md:group-hover:opacity-100 ${theme === 'dark'
                                                    ? 'bg-gray-700 hover:bg-gray-600 border-primary'
                                                    : 'bg-white hover:bg-gray-100 border-primary'
                                                    } border shadow-sm`}
                                                aria-label={t('TOOLS_REMOVE_TOOLS') || 'Remove tools'}
                                            >
                                                <Xmark className={`h-2.5 w-2.5 ${theme === 'dark' ? 'text-[#ffffff]' : 'text-[#3D3D3D]'}`} />
                                            </button>
                                        </div>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p className="text-xs">
                                            {t('TOOLS_SELECTED_COUNT', { count: selectedTools.length }) ||
                                                `${selectedTools.length} tools selected`}
                                        </p>
                                    </TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        </div>
                    )}
                </div>
            )}
            {renderToolsFlyoutPanel()}
            {renderDesktopToolsIndicatorPanel()}
        </div>
    );
};
